import type { CompanyDetail } from '@planuze/admin-contracts';
import { Button, EmptyState, InlineAlert } from '@planuze/ui';
import { CreditCard } from 'lucide-react';
import { useState } from 'react';
import { type CompaniesContent, useCompaniesContent } from '../../content/sections/companies.js';
import { type Currency, fmtCurrency, fmtDate } from '../../lib/format.js';
import { AssignPlanModal } from './assign-plan-modal.js';

type Subscription = NonNullable<CompanyDetail['subscription']>;

/* `fmtCurrency` espera a unidade maior (reais/dólares); a row do Stripe guarda cents. */
const CENTS_PER_UNIT = 100;

/* Moedas com formatação locale-aware garantida (`fmtCurrency`). Fora dessas caímos num
   render genérico com o código ISO cru — nunca escondemos o preço. */
function toKnownCurrency(currency: string): Currency | null {
  const upper = currency.toUpperCase();
  return upper === 'BRL' || upper === 'USD' ? upper : null;
}

function formatPrice(priceCents: number, currency: string): string {
  const amount = priceCents / CENTS_PER_UNIT;
  const known = toKnownCurrency(currency);
  return known !== null
    ? fmtCurrency(amount, known)
    : `${amount.toFixed(2)} ${currency.toUpperCase()}`;
}

function Field({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

/* Detalhe da assinatura: plano/preço/período (enriquecidos do billing, ADR-0347) + avisos de
   cancelamento e mudança de plano agendados. Só mostra o que a row realmente tem. */
function SubscriptionDetails({
  subscription,
  copy,
}: {
  readonly subscription: Subscription;
  readonly copy: CompaniesContent;
}) {
  const {
    planDisplayName,
    planTier,
    status,
    quantity,
    priceCents,
    currency,
    currentPeriodStart,
    currentPeriodEnd,
    cancelAt,
    pendingPlan,
    pendingPlanAt,
  } = subscription;
  const hasPrice = priceCents !== null && currency !== null;
  return (
    <div className="space-y-4">
      {cancelAt !== null && (
        <InlineAlert variant="warning" title={copy.subscriptionTab.cancelScheduledTitle}>
          {copy.subscriptionTab.cancelScheduledDescription(fmtDate(cancelAt))}
        </InlineAlert>
      )}
      {pendingPlan !== null && (
        <InlineAlert variant="warning" title={copy.subscriptionTab.planChangeScheduledTitle}>
          {copy.subscriptionTab.planChangeTo} <span className="font-mono">{pendingPlan}</span>
          {copy.subscriptionTab.planChangeSuffix(
            pendingPlanAt !== null ? fmtDate(pendingPlanAt) : null,
          )}
        </InlineAlert>
      )}
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <Field label={copy.subscriptionTab.status} value={status} />
        <Field label={copy.detailHeader.summarySeats} value={String(quantity)} />
        {planDisplayName !== null && (
          <Field label={copy.subscriptionTab.plan} value={planDisplayName} />
        )}
        {planTier !== null && <Field label={copy.subscriptionTab.tier} value={planTier} />}
        {hasPrice && (
          <Field label={copy.subscriptionTab.price} value={formatPrice(priceCents, currency)} />
        )}
        {currentPeriodStart !== null && (
          <Field label={copy.subscriptionTab.periodStart} value={fmtDate(currentPeriodStart)} />
        )}
        {currentPeriodEnd !== null && (
          <Field label={copy.subscriptionTab.periodEnd} value={fmtDate(currentPeriodEnd)} />
        )}
      </dl>
    </div>
  );
}

/* Aba Assinatura: leitura da subscription + (C6, ADR-0422) atribuir o plano custom da
   org (super_admin/billing_ops via `canAssign`). */
export function CompanySubscriptionTab({
  subscription,
  companyId,
  companyName,
  canAssign,
}: {
  readonly subscription: Subscription | null;
  readonly companyId: string;
  readonly companyName: string;
  readonly canAssign: boolean;
}) {
  const copy = useCompaniesContent();
  const [assignOpen, setAssignOpen] = useState(false);
  return (
    <div className="space-y-4">
      {canAssign && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            {copy.subscriptionTab.assignPlan}
          </Button>
        </div>
      )}
      {subscription === null ? (
        <EmptyState
          icon={<CreditCard className="size-6" />}
          title={copy.subscriptionTab.emptyTitle}
          description={copy.subscriptionTab.emptyDescription}
        />
      ) : (
        <SubscriptionDetails subscription={subscription} copy={copy} />
      )}
      <AssignPlanModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        companyId={companyId}
        companyName={companyName}
      />
    </div>
  );
}
